import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { EntityDisplayLinks } from '../shared/EntityDisplayLink';
import {
  entityLifecycle,
  entityTitle,
  entityType,
  LifecycleCell,
  TypeCell,
} from '../shared/entityCells';
import { isAppPath } from '../shared/links';
import { useByxTableStyles } from '../shared/table';
import { ByxMessageKey, useByxT } from '../shared/translations';
import { openSans } from '../theme/fonts';
import { byxScheme } from '../theme/tokens';
import { ByxCard, CardError, TableSkeleton } from './ByxCard';

/**
 * Relation table panels (Sprint 3): one table panel per section, each with
 * its count, over the relations of the page's entity. Shared by the
 * Componente Dependências and API tabs, Itens da squad and Partes do
 * sistema. Every related entity is read in one batched call, a section with
 * no item is not rendered, and a whole row opens its entity (SR-13).
 */

/** One panel: the targets of a relation, optionally of one kind only. */
export type RelationSection = {
  id: string;
  relation: string;
  /** Only targets of this kind, e.g. the components among `ownerOf`. */
  kind?: string;
  titleKey: ByxMessageKey;
};

/**
 * Column plan in px. Nome takes the free width, at least `name`; Dono is
 * shown only when it has a width.
 */
export type RelationColumns = {
  name: number;
  owner?: number;
  type: number;
  lifecycle: number;
};

/** The section's target refs, as strings, in the entity's order. */
export function sectionRefs(
  entity: Entity,
  section: RelationSection,
): string[] {
  return getEntityRelations(
    entity,
    section.relation,
    section.kind ? { kind: section.kind } : undefined,
  ).map(ref => stringifyEntityRef(ref));
}

/** True when any section has at least one target. */
export function hasRelationItems(
  entity: Entity,
  sections: readonly RelationSection[],
): boolean {
  return sections.some(section => sectionRefs(entity, section).length > 0);
}

/**
 * Fields read for each related entity. Only the display name of a profile,
 * never the rest of it (C2-1, MC-2).
 */
export const RELATED_ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.description',
  'spec.type',
  'spec.lifecycle',
  'spec.profile.displayName',
  'relations',
];

export type RelatedEntitiesState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; byRef: Map<string, Entity> };

/**
 * Every entity the sections point at, in one batched call (never one call
 * per row), and a way to load them again.
 */
export function useRelatedEntities(
  entity: Entity,
  sections: readonly RelationSection[],
): { state: RelatedEntitiesState; retry: () => void } {
  const catalogApi = useApi(catalogApiRef);
  const refs = useMemo(
    () => [
      ...new Set(sections.flatMap(section => sectionRefs(entity, section))),
    ],
    [entity, sections],
  );
  const key = refs.join(',');
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<RelatedEntitiesState>({
    status: 'loading',
  });
  useEffect(() => {
    let active = true;
    if (refs.length === 0) {
      setState({ status: 'ready', byRef: new Map() });
      return undefined;
    }
    setState({ status: 'loading' });
    catalogApi
      .getEntitiesByRefs({ entityRefs: refs, fields: RELATED_ENTITY_FIELDS })
      .then(
        ({ items }) => {
          const byRef = new Map<string, Entity>();
          items.forEach((item, index) => {
            if (item) {
              byRef.set(refs[index], item);
            }
          });
          if (active) {
            setState({ status: 'ready', byRef });
          }
        },
        () => active && setState({ status: 'error' }),
      );
    return () => {
      active = false;
    };
    // `refs` is derived from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, key, version]);
  const retry = useCallback(() => setVersion(value => value + 1), []);
  return { state, retry };
}

/**
 * Whole rows open the entity: a click is delegated to the row's first link,
 * and only app paths are followed, through the router (SR-13). Clicks on
 * links and controls, and modified clicks, stay with the browser.
 */
export function useRowNavigation(rowSelector: string = 'tbody tr') {
  const navigate = useNavigate();
  return useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest('a, button, input, select, textarea, [tabindex]')) {
        return;
      }
      const href = target
        .closest(rowSelector)
        ?.querySelector<HTMLAnchorElement>('a[href]')
        ?.getAttribute('href');
      if (isAppPath(href)) {
        navigate(href);
      }
    },
    [navigate, rowSelector],
  );
}

type Row = { ref: string; entity?: Entity };

const useStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    table: {
      tableLayout: 'fixed',
      '& th': { padding: theme.spacing(1.5), whiteSpace: 'nowrap' },
      '& td': { padding: theme.spacing(1.75, 1.5) },
      '& th, & td': { borderBottom: `1px solid ${scheme.line}` },
      '& tbody tr:last-child td': { borderBottom: 0 },
      '& th:first-child, & td:first-child': {
        paddingLeft: theme.spacing(2.5),
      },
      '& tbody tr': { cursor: 'pointer' },
      '& tbody tr:hover': { backgroundColor: scheme.hover },
    },
    nameLink: {
      color: scheme.accent,
    },
    // Sistema Dono (UI spec): 14px 500 primary link, one line (U3-3).
    ownerLink: {
      display: 'block',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 14,
      fontWeight: 500,
      color: scheme.accent,
      '& a': { color: scheme.accent, fontWeight: 500 },
    },
    none: {
      fontSize: 13,
      color: scheme.mutedText,
    },
    // Squad and Sistema title rows (UI spec): 12px 16px 12px 20px.
    denseHead: {
      padding: theme.spacing(1.5, 2, 1.5, 2.5),
    },
    // Squad and Sistema cells (UI spec): padding 12px, first column 20px.
    pageTable: {
      '& td': { padding: theme.spacing(1.5) },
      '& td:first-child': { paddingLeft: theme.spacing(2.5) },
    },
    // Squad and Sistema Tipo (UI spec): body-large in ink.
    pageType: {
      '& span:not($none)': { fontSize: 14, color: scheme.ink },
    },
    panel: {
      backgroundColor: scheme.panel,
      border: `1px solid ${scheme.line}`,
      borderRadius: 14,
      overflow: 'hidden',
    },
    skeletonHead: {
      padding: theme.spacing(1.5, 2, 1.5, 2.5),
      borderBottom: `1px solid ${scheme.line}`,
    },
    skeletonTitle: {
      display: 'block',
      width: 120,
      height: 14,
      margin: theme.spacing(0.5, 0),
      borderRadius: 6,
      backgroundColor: scheme.surface2,
      animation: '$pulse 1.4s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.55 },
    },
    // Empty and error panels (UI spec): centred, 48px padding, body-large.
    message: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(6),
      textAlign: 'center',
    },
    messageText: {
      margin: 0,
      fontFamily: openSans,
      fontSize: 14,
      lineHeight: '21px',
    },
    muted: { color: scheme.mutedText },
    ink: { color: scheme.ink },
    errorIcon: {
      fontSize: 20,
      marginBottom: theme.spacing(1),
      color: scheme.error,
    },
    retry: {
      height: 36,
      marginTop: theme.spacing(2),
      textTransform: 'none',
      fontSize: 13,
      borderColor: scheme.borderStrong,
      color: scheme.ink,
    },
    visuallyHidden: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      border: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
  };
});

/** Widths of the column plan, as classes (no inline style, R-17). */
const useColumnStyles = makeStyles({
  table: {
    minWidth: (c: RelationColumns) =>
      c.name + (c.owner ?? 0) + c.type + c.lifecycle,
  },
  name: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    minWidth: (c: RelationColumns) => c.name,
  },
  owner: { width: (c: RelationColumns) => c.owner ?? 0 },
  type: { width: (c: RelationColumns) => c.type },
  lifecycle: { width: (c: RelationColumns) => c.lifecycle },
});

function NameCell(props: Readonly<{ row: Row; columns: RelationColumns }>) {
  const { row, columns } = props;
  const table = useByxTableStyles();
  const classes = useStyles();
  const columnClasses = useColumnStyles(columns);
  const entityRoute = useRouteRef(entityRouteRef);
  const compound = parseEntityRef(row.ref);
  const title = row.entity ? entityTitle(row.entity) : compound.name;
  const description = row.entity?.metadata.description;
  return (
    <div className={columnClasses.name}>
      <span className={table.nameTitle} title={title}>
        <Link
          to={entityRoute(entityRouteParams(compound))}
          className={classes.nameLink}
        >
          {title}
        </Link>
      </span>
      {description && (
        <span className={table.description} title={description}>
          {description}
        </span>
      )}
    </div>
  );
}

/**
 * The Dono cell: the muted cell of the Componente tabs (Dependências), or
 * on the Squad / Sistema pages the 14px 500 primary link (U3-3).
 */
function OwnerCell({
  entity,
  page,
}: Readonly<{ entity?: Entity; page: boolean }>) {
  const table = useByxTableStyles();
  const classes = useStyles();
  const owners = entity ? getEntityRelations(entity, RELATION_OWNED_BY) : [];
  if (owners.length === 0) {
    return null;
  }
  return (
    <EntityDisplayLinks
      entityRefs={owners}
      defaultKind="group"
      className={page ? classes.ownerLink : table.cell}
    />
  );
}

/** "—" in muted text, for an empty Tipo or Ciclo de vida (Squad, Sistema). */
function NoValue() {
  const classes = useStyles();
  const { t } = useByxT();
  return <span className={classes.none}>{t('entity.table.none')}</span>;
}

function SectionPanel(
  props: Readonly<{
    title: string;
    rows: Row[];
    columns: RelationColumns;
    dash: boolean;
    action?: ReactNode;
    headClassName?: string;
  }>,
) {
  const { title, rows, columns, dash, action, headClassName } = props;
  const classes = useStyles();
  const columnClasses = useColumnStyles(columns);
  const { t } = useByxT();
  const showOwner = columns.owner !== undefined;
  return (
    <ByxCard
      title={title}
      count={rows.length}
      variant="table"
      actions={action}
      headClassName={headClassName}
    >
      <Table
        className={[
          classes.table,
          columnClasses.table,
          dash ? classes.pageTable : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={title}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('catalog.columns.name')}</TableCell>
            {showOwner && (
              <TableCell className={columnClasses.owner}>
                {t('catalog.columns.owner')}
              </TableCell>
            )}
            <TableCell className={columnClasses.type}>
              {t('catalog.columns.type')}
            </TableCell>
            <TableCell className={columnClasses.lifecycle}>
              {t('catalog.columns.lifecycle')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const hasType = !!row.entity && !!entityType(row.entity);
            const hasLifecycle = !!row.entity && !!entityLifecycle(row.entity);
            return (
              <TableRow key={row.ref}>
                <TableCell>
                  <NameCell row={row} columns={columns} />
                </TableCell>
                {showOwner && (
                  <TableCell>
                    <OwnerCell entity={row.entity} page={dash} />
                  </TableCell>
                )}
                <TableCell className={dash ? classes.pageType : undefined}>
                  {hasType && <TypeCell entity={row.entity!} />}
                  {!hasType && dash && <NoValue />}
                </TableCell>
                <TableCell>
                  {hasLifecycle && <LifecycleCell entity={row.entity!} />}
                  {!hasLifecycle && dash && <NoValue />}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ByxCard>
  );
}

/** Squad / Sistema loading: two panels, a bar in place of each title. */
function PanelsSkeleton(props: Readonly<{ label: string; columns: number }>) {
  const classes = useStyles();
  return (
    <output className={classes.root}>
      <span className={classes.visuallyHidden}>{props.label}</span>
      {[0, 1].map(panel => (
        <div key={panel} className={classes.panel} aria-hidden="true">
          <div className={classes.skeletonHead}>
            <span className={classes.skeletonTitle} />
          </div>
          <TableSkeleton columns={props.columns} />
        </div>
      ))}
    </output>
  );
}

/** Squad / Sistema error: the message and "Tentar de novo" (no raw error). */
export function PanelError(
  props: Readonly<{ message: string; onRetry: () => void }>,
) {
  const classes = useStyles();
  const { t } = useByxT();
  return (
    <div className={`${classes.panel} ${classes.message}`} role="alert">
      <ErrorIcon aria-hidden className={classes.errorIcon} />
      <p className={`${classes.messageText} ${classes.ink}`}>{props.message}</p>
      <Button
        variant="outlined"
        className={classes.retry}
        onClick={props.onRetry}
      >
        {t('entity.panels.retry')}
      </Button>
    </div>
  );
}

/** The all-empty panel: the message only, no icon and no link. */
export function PanelEmpty(props: Readonly<{ message: string }>) {
  const classes = useStyles();
  return (
    <div className={`${classes.panel} ${classes.message}`}>
      <p className={`${classes.messageText} ${classes.muted}`}>
        {props.message}
      </p>
    </div>
  );
}

export type RelationPanelsProps = {
  entity: Entity;
  /** The group's name: the loading label and the card-state title. */
  title: string;
  sections: readonly RelationSection[];
  columns: RelationColumns;
  /** Title-row action of a section, e.g. "Ver no catálogo". */
  renderSectionAction?: (section: RelationSection) => ReactNode;
  /**
   * The page states of Squad and Sistema: two skeleton panels while
   * loading, the error message with "Tentar de novo", the all-empty
   * message, "—" for an empty Tipo or Ciclo de vida, and the denser title
   * row. Without it the card states of the Componente tabs are used, and
   * nothing renders when every section is empty.
   */
  pageStates?: { error: string; empty: string };
};

export function RelationPanels(props: Readonly<RelationPanelsProps>) {
  const { entity, title, sections, columns, renderSectionAction, pageStates } =
    props;
  const { t } = useByxT();
  const classes = useStyles();
  const handleClick = useRowNavigation();
  const { state, retry } = useRelatedEntities(entity, sections);
  const columnCount = columns.owner === undefined ? 3 : 4;
  const loadingLabel = t('entity.card.loading', { card: title });

  if (state.status === 'loading') {
    return pageStates ? (
      <PanelsSkeleton label={loadingLabel} columns={columnCount} />
    ) : (
      <ByxCard title={title} variant="table">
        <TableSkeleton label={loadingLabel} columns={columnCount} />
      </ByxCard>
    );
  }
  if (state.status === 'error') {
    return pageStates ? (
      <PanelError message={pageStates.error} onRetry={retry} />
    ) : (
      <ByxCard title={title} variant="table">
        <CardError title={title} variant="table" />
      </ByxCard>
    );
  }

  const shown = sections
    .map(section => ({
      section,
      rows: sectionRefs(entity, section).map(ref => ({
        ref,
        entity: state.byRef.get(ref),
      })),
    }))
    .filter(({ rows }) => rows.length > 0);

  if (shown.length === 0) {
    return pageStates ? <PanelEmpty message={pageStates.empty} /> : null;
  }

  return (
    // The div is only a delegation surface; the keyboard target stays the
    // real link inside the row.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={classes.root} onClick={handleClick}>
      {shown.map(({ section, rows }) => (
        <SectionPanel
          key={section.id}
          title={t(section.titleKey)}
          rows={rows}
          columns={columns}
          dash={!!pageStates}
          action={renderSectionAction?.(section)}
          headClassName={pageStates ? classes.denseHead : undefined}
        />
      ))}
    </div>
  );
}
